/**
 * Hộp "Phát khẩn cấp" mở từ nút Phát ngay của trang Cụm loa (DV003).
 *
 * Liệt kê bản tin đã duyệt; bấm "Phát khẩn cấp" ở một dòng → hộp xác nhận nhỏ
 * "Phát thiết bị" nêu đích phát → Đồng ý gửi `POST /api/v1/broadcast/emergency`.
 *   - Có tích chọn thiết bị: gửi `cumLoaId` = CumLoaID các thiết bị đó, bộ tương thích
 *     nhận `DanhSachThietBi` mang đúng các mã này.
 *   - Không chọn: phát toàn bộ địa bàn đang chọn, `DanhSachThietBi` rỗng (Công văn 276
 *     mục 3.3).
 */
import { useId, useState, type ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viThietBi } from '../ngon-ngu/vi-thiet-bi';
import { viBienTapNoiDung } from '../ngon-ngu/vi-bien-tap';
import { banTinApi, LOAI_BAN_TIN_CUM_LOA, TRANG_THAI_CHON_PHAT, type DongBanTin } from '../api/ban-tin-api';
import { phatSongApi } from '../api/phat-song-api';
import type { DongThietBi } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import { TrinhPhatAmThanh } from '../thanh-phan/TrinhPhatAmThanh';

export interface DichPhatKhanCap {
  /** Mã địa bàn khi không chọn thiết bị (rỗng = không xác định được phạm vi). */
  diaBan: string[];
  /** Tên hiển thị của địa bàn, vd "Tỉnh Phú Thọ". */
  tenDiaBan: string;
}

export interface HopPhatKhanCapCumLoaProps {
  thietBi: DongThietBi[];
  dich: DichPhatKhanCap;
  onDong: () => void;
  onDaGui: (thongBao: string) => void;
  api?: {
    danhSachBanTin: typeof banTinApi.danhSach;
    doanAmThanh: typeof banTinApi.doanAmThanh.danhSach;
    phatKhanCap: typeof phatSongApi.phatKhanCap;
  };
}

const API_MAC_DINH: NonNullable<HopPhatKhanCapCumLoaProps['api']> = {
  danhSachBanTin: banTinApi.danhSach,
  doanAmThanh: banTinApi.doanAmThanh.danhSach,
  phatKhanCap: phatSongApi.phatKhanCap,
};

function loiThanhChu(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function HopPhatKhanCapCumLoa({
  thietBi,
  dich,
  onDong,
  onDaGui,
  api = API_MAC_DINH,
}: HopPhatKhanCapCumLoaProps): ReactNode {
  const t = viThietBi.phatKhanCap;
  const nhanBt = viBienTapNoiDung;
  const idTieuDe = useId();
  const [hoi, datHoi] = useState<string | null>(null);
  const [dangGui, datDangGui] = useState(false);
  const [loi, datLoi] = useState<string | null>(null);
  const [nghe, datNghe] = useState<{ id: string; tieuDe: string; url: string | null } | null>(null);

  const ds = dungTaiDuLieu<DongBanTin[]>({
    tai: async (signal) =>
      (
        await api.danhSachBanTin(
          { trangThai: TRANG_THAI_CHON_PHAT, loaiBanTin: LOAI_BAN_TIN_CUM_LOA, pageSize: 50 },
          signal,
        )
      ).items,
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const coThietBi = thietBi.length > 0;
  const moTaDich = coThietBi
    ? dienVao(t.dichThietBi, {
        so: thietBi.length,
        ds: thietBi
          .slice(0, 3)
          .map((d) => d.tenThietBi || d.cumLoaId)
          .join(', '),
        them: thietBi.length > 3 ? '…' : '',
      })
    : dienVao(t.dichDiaBan, { ten: dich.tenDiaBan });
  const khongCoDich = !coThietBi && dich.diaBan.length === 0;

  const xemLai = async (b: DongBanTin): Promise<void> => {
    if (nghe?.id === b.id) {
      datNghe(null);
      return;
    }
    datNghe({ id: b.id, tieuDe: b.tieuDe, url: null });
    try {
      const doan = await api.doanAmThanh(b.id);
      datNghe({ id: b.id, tieuDe: b.tieuDe, url: doan.find((x) => x.urlNgheThu)?.urlNgheThu ?? null });
    } catch {
      datNghe({ id: b.id, tieuDe: b.tieuDe, url: null });
    }
  };

  const phat = async (b: DongBanTin): Promise<void> => {
    datDangGui(true);
    datLoi(null);
    try {
      await api.phatKhanCap({
        banTinId: b.id,
        phamVi: coThietBi ? [] : dich.diaBan.map((ma) => ({ DichID: ma })),
        ...(coThietBi ? { cumLoaId: thietBi.map((d) => d.cumLoaId) } : {}),
        lyDo: dienVao(t.lyDo, { dich: moTaDich }),
      });
      datHoi(null);
      onDaGui(dienVao(t.daGui, { tieuDe: b.tieuDe, dich: moTaDich }));
    } catch (e) {
      datLoi(dienVao(t.loi, { loi: loiThanhChu(e) }));
    } finally {
      datDangGui(false);
    }
  };

  return (
    <div className="man-che" role="presentation">
      <div
        className="hop-thoai tb-phat-kc"
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTieuDe}
        data-vung="phat-khan-cap-cum-loa"
      >
        <div className="tb-phat-kc__dau">
          <h2 id={idTieuDe}>{t.tieuDe}</h2>
          <button type="button" className="tb-phat-kc__dong" aria-label={t.dong} onClick={onDong}>
            ×
          </button>
        </div>
        <p className="tb-phat-kc__dich" data-vung="dich-phat">
          {moTaDich}
        </p>
        {khongCoDich ? <p className="tb-phat-kc__loi">{t.chuaCoDiaBan}</p> : null}
        {loi ? (
          <p className="tb-phat-kc__loi" role="alert">
            {loi}
          </p>
        ) : null}

        <div className="tb-phat-kc__bang">
          <table className="bang">
            <thead>
              <tr>
                <th className="bang__giua">{t.cot.stt}</th>
                <th>{t.cot.tieuDe}</th>
                <th className="bang__giua">{t.cot.thoiLuong}</th>
                <th>{t.cot.loaiBanTin}</th>
                <th>{t.cot.mucDoUuTien}</th>
                <th>{t.cot.loaiLinhVuc}</th>
                <th className="bang__giua">{t.cot.xemLai}</th>
                <th>{t.cot.nguoiTao}</th>
                <th className="bang__giua">{t.cot.thaoTac}</th>
              </tr>
            </thead>
            <tbody>
              {ds.trangThai === 'dang-tai' ? (
                <tr>
                  <td colSpan={9}>{t.dangTai}</td>
                </tr>
              ) : ds.trangThai === 'loi' ? (
                <tr>
                  <td colSpan={9} className="tb-phat-kc__loi">
                    {t.loiTai}
                  </td>
                </tr>
              ) : (ds.duLieu ?? []).length === 0 ? (
                <tr>
                  <td colSpan={9}>{t.rong}</td>
                </tr>
              ) : (
                (ds.duLieu ?? []).map((b, i) => (
                  <tr key={b.id} data-ban-tin={b.banTinId}>
                    <td className="bang__giua">{i + 1}</td>
                    <td>{b.tieuDe}</td>
                    <td className="bang__giua">{b.thoiLuong ?? '—'}</td>
                    <td>
                      {nhanBt.loaiBanTinNhan[b.loaiBanTin as keyof typeof nhanBt.loaiBanTinNhan] ??
                        b.loaiBanTin}
                    </td>
                    <td>
                      {nhanBt.uuTienNhan[b.mucDoUuTien as keyof typeof nhanBt.uuTienNhan] ?? b.mucDoUuTien}
                    </td>
                    <td>
                      {vi.banTin.linhVucNhan[b.loaiLinhVuc as keyof typeof vi.banTin.linhVucNhan] ??
                        b.loaiLinhVuc}
                    </td>
                    <td className="bang__giua">
                      <button
                        type="button"
                        className="tb-phat-kc__xem"
                        aria-label={dienVao(t.xemLaiNhan, { tieuDe: b.tieuDe })}
                        aria-pressed={nghe?.id === b.id}
                        onClick={() => void xemLai(b)}
                      >
                        <BieuTuong ten="mat" co={18} />
                      </button>
                    </td>
                    <td className="tb-phat-kc__nguoi-tao">{b.tenNguoiTao ?? '—'}</td>
                    <td className="bang__giua tb-phat-kc__o-nut">
                      <button
                        type="button"
                        className="nut nut--chinh tb-phat-kc__nut"
                        data-hanh-dong="phat-khan-cap"
                        disabled={dangGui || khongCoDich}
                        onClick={() => datHoi(hoi === b.id ? null : b.id)}
                      >
                        <BieuTuong ten="phat-song" co={14} />
                        {t.phatKhanCap}
                      </button>
                      {hoi === b.id ? (
                        <div className="tb-phat-kc__hoi" role="alertdialog" aria-label={t.xacNhanTieuDe}>
                          <strong>
                            <span className="tb-phat-kc__cham" aria-hidden="true">
                              !
                            </span>
                            {t.xacNhanTieuDe}
                          </strong>
                          <p>{moTaDich}</p>
                          <div className="tb-phat-kc__hoi-nut">
                            <button type="button" className="nut nut--nho" onClick={() => datHoi(null)}>
                              {t.huy}
                            </button>
                            <button
                              type="button"
                              className="nut nut--nho nut--chinh"
                              data-hanh-dong="dong-y-phat"
                              disabled={dangGui}
                              onClick={() => void phat(b)}
                            >
                              {dangGui ? t.dangGui : t.dongY}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {nghe ? (
          <div className="tb-phat-kc__nghe">
            {nghe.url ? (
              <TrinhPhatAmThanh nhan={nghe.tieuDe} url={nghe.url} />
            ) : (
              <p>{dienVao(t.chuaCoAmThanh, { tieuDe: nghe.tieuDe })}</p>
            )}
          </div>
        ) : null}

        <p className="tb-phat-kc__tong">{dienVao(t.tong, { so: (ds.duLieu ?? []).length })}</p>
      </div>
    </div>
  );
}
